/**
 * Composable for username generation and validation
 */

/**
 * Generates a valid username from a full name in PascalCase format
 * @param fullName - The full name to generate username from
 * @returns A valid username string
 * @example
 * generateUsername('John Doe') // returns 'JohnDoe'
 * generateUsername('Marie-Claire Dubois') // returns 'MareClaireDubois'
 */
export function generateUsername(fullName: string): string {
  if (!fullName) return ''

  // Split by spaces and filter out empty strings
  const words = fullName
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0)

  if (words.length === 0) return ''

  // Process each word: remove special chars, then capitalize first letter
  const processedWords = words
    .map((word) => {
      // Remove all non-alphanumeric characters
      const cleanWord = word.replace(/[^a-zA-Z0-9]/g, '')

      if (!cleanWord) return ''

      // Capitalize first letter, lowercase the rest
      return cleanWord.charAt(0).toUpperCase() + cleanWord.slice(1).toLowerCase()
    })
    .filter((word) => word.length > 0) // Remove empty words

  let username = processedWords.join('')

  // Ensure it doesn't start with a number
  if (/^[0-9]/.test(username)) {
    username = 'User' + username
  }

  // Trim to max length
  if (username.length > 30) {
    username = username.substring(0, 30)
  }

  return username
}

/**
 * Validates a username according to the rules
 * @param value - The username to validate
 * @returns Error message if invalid, null if valid
 */
export function validateUsername(value: string): string | null {
  if (!value) {
    return 'Username is required.'
  }

  if (value.length < 3) {
    return 'Username must be at least 3 characters.'
  }

  if (value.length > 30) {
    return 'Username must not exceed 30 characters.'
  }

  if (/^[0-9]/.test(value)) {
    return 'Username should not start with a number.'
  }

  if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(value)) {
    return 'Username should only contain letters, numbers, or underscore.'
  }

  return null
}
